import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ScanLine,
  Package,
  Wrench,
  Layers,
  AlertCircle,
  Clock,
  MapPin,
  ArrowRight,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/common';
import { toast } from 'sonner';
import {
  barcodeApi,
  ScanResult,
  ScanResultType,
  InventoryItemScanResult,
  EquipmentScanResult,
  BatchScanResult,
} from '@/api/services/barcode.api';

interface ScanHistoryItem {
  code: string;
  result: ScanResult;
  timestamp: Date;
}

export default function QuickScan() {
  const [scanCode, setScanCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [currentResult, setCurrentResult] = useState<ScanResult | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanHistoryItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleScan = useCallback(async () => {
    if (!scanCode.trim()) return;

    setIsScanning(true);
    try {
      const response = await barcodeApi.scan(scanCode.trim());
      const result = response.data;
      setCurrentResult(result);

      // Add to history
      setScanHistory((prev) => [
        { code: scanCode.trim(), result, timestamp: new Date() },
        ...prev.slice(0, 9), // Keep last 10
      ]);

      if (result.type === ScanResultType.NOT_FOUND) {
        toast.error(result.message || 'No item found for this barcode');
      } else {
        toast.success(`Found ${result.type.replace('_', ' ').toLowerCase()}`);
      }
    } catch {
      toast.error('Failed to scan barcode. Please try again.');
    } finally {
      setIsScanning(false);
      setScanCode('');
      inputRef.current?.focus();
    }
  }, [scanCode]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  const clearHistory = () => {
    setScanHistory([]);
    setCurrentResult(null);
  };

  const navigateToDetail = (result: ScanResult) => {
    switch (result.type) {
      case ScanResultType.INVENTORY_ITEM:
        navigate(`/inventory/${result.inventoryItem?.id}`);
        break;
      case ScanResultType.EQUIPMENT:
        navigate(`/equipment/${result.equipment?.id}`);
        break;
      case ScanResultType.BATCH:
        navigate(`/inventory/${result.batch?.itemId}`);
        break;
    }
  };

  const getResultIcon = (type: ScanResultType) => {
    switch (type) {
      case ScanResultType.INVENTORY_ITEM:
        return <Package className="h-5 w-5 text-blue-500" />;
      case ScanResultType.EQUIPMENT:
        return <Wrench className="h-5 w-5 text-purple-500" />;
      case ScanResultType.BATCH:
        return <Layers className="h-5 w-5 text-green-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getResultTypeLabel = (type: ScanResultType) => {
    switch (type) {
      case ScanResultType.INVENTORY_ITEM:
        return 'Inventory Item';
      case ScanResultType.EQUIPMENT:
        return 'Equipment';
      case ScanResultType.BATCH:
        return 'Batch';
      default:
        return 'Not Found';
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Quick Scan"
        description="Scan barcodes to quickly lookup inventory items, equipment, or batches"
      />

      {/* Scan Input */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <ScanLine className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={scanCode}
                onChange={(e) => setScanCode(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Scan or enter barcode..."
                className="h-14 pl-10 text-lg"
                autoFocus
              />
            </div>
            <Button
              onClick={handleScan}
              disabled={isScanning || !scanCode.trim()}
              className="h-14 px-8"
            >
              {isScanning ? 'Scanning...' : 'Scan'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current Result */}
      {currentResult && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="flex items-center gap-2">
              {getResultIcon(currentResult.type)}
              {getResultTypeLabel(currentResult.type)}
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentResult(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {currentResult.type === ScanResultType.INVENTORY_ITEM &&
              currentResult.inventoryItem && (
                <InventoryItemResult
                  item={currentResult.inventoryItem}
                  onNavigate={() => navigateToDetail(currentResult)}
                />
              )}

            {currentResult.type === ScanResultType.EQUIPMENT &&
              currentResult.equipment && (
                <EquipmentResult
                  equipment={currentResult.equipment}
                  onNavigate={() => navigateToDetail(currentResult)}
                />
              )}

            {currentResult.type === ScanResultType.BATCH &&
              currentResult.batch && (
                <BatchResult
                  batch={currentResult.batch}
                  onNavigate={() => navigateToDetail(currentResult)}
                />
              )}

            {currentResult.type === ScanResultType.NOT_FOUND && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertCircle className="h-5 w-5" />
                <span>{currentResult.message}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Scan History */}
      {scanHistory.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Recent Scans</CardTitle>
            <Button variant="ghost" size="sm" onClick={clearHistory}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scanHistory.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getResultIcon(item.result.type)}
                        <span className="text-sm">
                          {getResultTypeLabel(item.result.type)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {item.code}
                    </TableCell>
                    <TableCell>
                      {item.result.inventoryItem?.name ||
                        item.result.equipment?.name ||
                        item.result.batch?.itemName ||
                        '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {item.timestamp.toLocaleTimeString()}
                    </TableCell>
                    <TableCell>
                      {item.result.type !== ScanResultType.NOT_FOUND && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigateToDetail(item.result)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Sub-components for different result types

function InventoryItemResult({
  item,
  onNavigate,
}: {
  item: InventoryItemScanResult;
  onNavigate: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">{item.name}</h3>
          <p className="text-sm text-muted-foreground">SKU: {item.sku}</p>
          {item.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {item.description}
            </p>
          )}
        </div>
        <Button onClick={onNavigate}>
          View Details
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Category</p>
          <p className="font-medium">{item.category}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Total Stock</p>
          <p className="font-medium">
            {item.totalStock} {item.unit}
          </p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Unit Cost</p>
          <p className="font-medium">
            {item.unitCost ? `$${item.unitCost.toFixed(2)}` : '-'}
          </p>
        </div>
      </div>

      {item.levels.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Stock by Location</h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Available</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {item.levels.map((level) => (
                <TableRow key={level.locationId}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      {level.locationName}
                      <span className="text-xs text-muted-foreground">
                        ({level.locationCode})
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{level.quantity}</TableCell>
                  <TableCell className="text-right">
                    {level.availableQty}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function EquipmentResult({
  equipment,
  onNavigate,
}: {
  equipment: EquipmentScanResult;
  onNavigate: () => void;
}) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800';
      case 'IN_USE':
        return 'bg-blue-100 text-blue-800';
      case 'MAINTENANCE':
        return 'bg-yellow-100 text-yellow-800';
      case 'REPAIR':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">{equipment.name}</h3>
          <p className="text-sm text-muted-foreground">
            Asset #: {equipment.assetNumber}
          </p>
          {equipment.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {equipment.description}
            </p>
          )}
        </div>
        <Button onClick={onNavigate}>
          View Details
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Badge className={getStatusColor(equipment.status)}>
          {equipment.status}
        </Badge>
        <Badge variant="outline">{equipment.condition}</Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Category</p>
          <p className="font-medium">{equipment.category}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Location</p>
          <p className="font-medium">{equipment.locationName}</p>
        </div>
        {equipment.manufacturer && (
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Manufacturer</p>
            <p className="font-medium">{equipment.manufacturer}</p>
          </div>
        )}
        {equipment.model && (
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Model</p>
            <p className="font-medium">{equipment.model}</p>
          </div>
        )}
      </div>

      {equipment.nextServiceDate && (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span>
            Next Service:{' '}
            {new Date(equipment.nextServiceDate).toLocaleDateString()}
          </span>
        </div>
      )}
    </div>
  );
}

function BatchResult({
  batch,
  onNavigate,
}: {
  batch: BatchScanResult;
  onNavigate: () => void;
}) {
  const isExpired =
    batch.expiryDate && new Date(batch.expiryDate) < new Date();
  const isExpiringSoon =
    batch.expiryDate &&
    new Date(batch.expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">Batch: {batch.batchNumber}</h3>
          <p className="text-sm text-muted-foreground">
            Item: {batch.itemName} ({batch.itemSku})
          </p>
        </div>
        <Button onClick={onNavigate}>
          View Item
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Badge variant="outline">{batch.status}</Badge>
        {isExpired && <Badge variant="destructive">Expired</Badge>}
        {!isExpired && isExpiringSoon && (
          <Badge className="bg-yellow-100 text-yellow-800">Expiring Soon</Badge>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Quantity</p>
          <p className="font-medium">{batch.quantity}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Location</p>
          <p className="font-medium">{batch.locationName}</p>
        </div>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Expiry Date</p>
          <p className="font-medium">
            {batch.expiryDate
              ? new Date(batch.expiryDate).toLocaleDateString()
              : 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );
}
